import React, { useMemo } from 'react';
import { TableConfig } from '../../../../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../../../../primary-record-types';
import { getIntl } from '../../../../../../utils/intl';
import { useRichTableState } from '../../../../../v2BaseComponents/RichTable/useRichTableState';
import RichTable from '../../../../../v2BaseComponents/RichTable';
import {
    useChainedSideDrawer,
    useMaybeRenderedSideDrawerToggle,
} from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import AddCareTeamMemberSideDrawerContent from './AddCareTeamMemberSideDrawerContent';
import { useResourcesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useIsOrganizationAdmin } from '../../../../../../hooks/api/useOrganization';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import EditCareTeamMemberSideDrawerContent from './EditCareTeamMemberSideDrawerContent';
import useOrganizationCareTeamMembers from '../../../../../../hooks/api/useOrganizationCareTeamMembers';
import CareTeamMemberDetailsSideDrawerContent from './CareTeamMemberDetailsSideDrawerContent';
import { isPresent } from 'ts-is-present';
import { getCareTeamMemberDescriptorDisplay } from '../../../../../../utils/careTeamUtils';
import { careTeamType } from '../../../../../../utils/sharedMessages';

const intl = getIntl('resources-root-screen-care-team-tab-table', {
    addCareTeamMember: 'Add Care Team Member',
    editCareTeamMember: 'Edit Care Team Member',
    careTeamMemberDetails: 'Care Team Member Details',
    name: 'Name',
    type: 'Type',
    deleteCareTeamMemberHeader: 'Delete Care Team Member?',
    deleteCareTeamMemberContent:
        'Are you sure you want to delete the care team member "{name}"?  This action cannot be undone.',
    deleteCareTeamMemberConfirm: 'Delete Care Team Member',
    cancel: 'Cancel',
    deleteCareTeamMembersHeader: 'Delete Care Team Members?',
    deleteCareTeamMembersContent:
        'Are you sure you want to delete {count} care team members?  This action cannot be undone.',
    deleteCareTeamMembersConfirm: 'Delete Care Team Members',
    delete: 'Delete',
});

export type CareTeamMembersTableOwnProps = {};

export type CareTeamMembersTableProps = CareTeamMembersTableOwnProps;

type TableItem = APITypes.organizationService.OrganizationCareTeamMemberDetails;

const CareTeamMembersTable: React.FC<CareTeamMembersTableProps> = () => {
    const resourcesTabNavigator = useResourcesTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const {
        organizationCareTeamMembers,
        deleteCareTeamMember,
        deleteCareTeamMembers,
        isDeletingCareTeamMember,
        isDeletingCareTeamMembers,
        isFetchingOrganizationCareTeamMembers,
    } = useOrganizationCareTeamMembers({});
    const getName = (careTeamMember: TableItem) => getCareTeamMemberDescriptorDisplay(careTeamMember) || '';
    const getType = (careTeamMember: TableItem) => careTeamType(careTeamMember.careTeamMemberType);
    const editCareTeamMemberSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editCareTeamMember',
        title: intl('editCareTeamMember'),
        renderContent: (onClose) => (
            <EditCareTeamMemberSideDrawerContent onClose={onClose} careTeamMemberId={tableState.selectedRowKeys[0]} />
        ),
        mobileOnFocus: () =>
            resourcesTabNavigator.navigate('edit-care-team-member', {
                careTeamMemberId: tableState.selectedRowKeys[0],
            }),
    });
    const chainedOnEdit = useChainedSideDrawer(editCareTeamMemberSideDrawer.onOpen);
    const careTeamMemberDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'careTeamMemberDetails',
        title: intl('careTeamMemberDetails'),
        renderContent: (onClose) => (
            <CareTeamMemberDetailsSideDrawerContent
                onClose={onClose}
                careTeamMemberId={tableState.selectedRowKeys[0]}
                onEdit={chainedOnEdit}
            />
        ),
        mobileOnFocus: () =>
            resourcesTabNavigator.navigate('care-team-member', { careTeamMemberId: tableState.selectedRowKeys[0] }),
    });
    const addCareTeamMemberSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addCareTeamMemberSideDrawerContent',
        title: intl('addCareTeamMember'),
        renderContent: (onClose) => <AddCareTeamMemberSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => resourcesTabNavigator.navigate('add-care-team-member'),
    });
    const isOrganizationAdmin = useIsOrganizationAdmin();
    const selectedCareTeamMembers = tableState.selectedRowKeys
        .map((key) => organizationCareTeamMembers.find((careTeamMember) => careTeamMember.careTeamMemberId === key))
        .filter(isPresent);
    const deleteButtonModal = usePromptButton({
        renderPromptContents: (onClose) => {
            const selectedCareTeamMember = selectedCareTeamMembers[0];
            if (!selectedCareTeamMember) return null;

            return (
                <ContentStack>
                    <InfoIcon
                        alertIconProps={{
                            name: 'AlertTriangle',
                            variant: 'error',
                        }}
                        title={
                            tableState.selectedRowKeys.length === 1
                                ? intl('deleteCareTeamMemberHeader')
                                : intl('deleteCareTeamMembersHeader')
                        }
                        subText={
                            tableState.selectedRowKeys.length === 1
                                ? intl('deleteCareTeamMemberContent', {
                                      name: getCareTeamMemberDescriptorDisplay(selectedCareTeamMember) || '',
                                  })
                                : intl('deleteCareTeamMemberContent', { count: tableState.selectedRowKeys.length })
                        }
                    />
                    <PromptActions
                        actions={[
                            {
                                title:
                                    tableState.selectedRowKeys.length === 1
                                        ? intl('deleteCareTeamMemberConfirm')
                                        : intl('deleteCareTeamMembersConfirm'),
                                onPress: () => {
                                    if (tableState.selectedRowKeys.length === 1) {
                                        deleteCareTeamMember(tableState.selectedRowKeys[0]);
                                    } else {
                                        deleteCareTeamMembers(tableState.selectedRowKeys);
                                    }
                                    onClose();
                                },
                                variant: 'alert',
                                icon: 'Trash2',
                                testID: 'delete-care-team-member-confirm',
                            },
                            {
                                title: intl('cancel'),
                                onPress: onClose,
                                variant: 'secondary',
                                icon: 'X',
                                testID: 'delete-care-team-member-cancel',
                            },
                        ]}
                    />
                </ContentStack>
            );
        },
        promptTitle:
            tableState.selectedRowKeys.length === 1
                ? intl('deleteCareTeamMemberHeader')
                : intl('deleteCareTeamMembersHeader'),
    });

    const careTeamMembersTableConfig: TableConfig<TableItem> = useMemo(
        () => ({
            columns: [
                {
                    label: intl('name'),
                    columnKey: 'name',
                    getSortValue: getName,
                    getContentKey: getName,
                    getSearchValue: getName,
                    render: getName,
                },
                {
                    label: intl('type'),
                    columnKey: 'type',
                    getSortValue: getType,
                    getContentKey: getType,
                    getSearchValue: getType,
                    getDiscreetValue: getType,
                    render: getType,
                },
            ],
            getRowKey: (careTeamMember) => careTeamMember.careTeamMemberId,
        }),
        [],
    );

    return (
        <>
            <RichTable
                config={careTeamMembersTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={organizationCareTeamMembers}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingOrganizationCareTeamMembers}
                actions={[
                    {
                        title: intl('addCareTeamMember'),
                        icon: 'PlusCircle',
                        onPress: () => {
                            addCareTeamMemberSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
                        testID: 'add-care-team-member-button',
                    },
                    {
                        title: intl('careTeamMemberDetails'),
                        icon: 'Info',
                        onPress: () => {
                            careTeamMemberDetailsSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => selected.length === 1,
                        testID: 'care-team-member-details',
                    },
                    {
                        title: intl('editCareTeamMember'),
                        icon: 'Edit2',
                        onPress: () => {
                            editCareTeamMemberSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 1,
                        testID: 'edit-care-team-member',
                    },
                    {
                        title: intl('delete'),
                        icon: 'Trash2',
                        onPress: deleteButtonModal.onPress,
                        shouldShow: (selected) => isOrganizationAdmin && selected.length > 0,
                        shouldDisable: () => isDeletingCareTeamMember || isDeletingCareTeamMembers,
                        testID: 'delete-care-team-member',
                    },
                ]}
            />
            {addCareTeamMemberSideDrawer.SideDrawer}
            {editCareTeamMemberSideDrawer.SideDrawer}
            {careTeamMemberDetailsSideDrawer.SideDrawer}
            {deleteButtonModal.Prompt}
        </>
    );
};

export default React.memo(CareTeamMembersTable);
